import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { removeLoadingKey } from '../../helpers/common'
import { toast } from 'react-toastify'

export const BudgetLoadingKeys = {
    getBudgetCards: "GET_ALL_BUDGET_CARDS",
    getBudgetSettings: "GET_BUDGET_SETTINGS",
    getBudgetHistory: "GET_BUDGET_HISTORY",
    getBudgetCard: "GET_BUDGET_CARD",
    duplicateBudget: "DUPLICATE_BUDGET",
    createBudgetCategory: "CREATE_BUDGET_CATEGORY",
    createTemplate: "CREATE_TEMPLATE",
    updateTemplate: "UPDATE_TEMPLATE",
    deleteTemplate: "DELETE_TEMPLATE",
    createNetIncome: "CREATE_NET_INCOME",
    updateNetIncome: "UPDATE_NET_INCOME",
    deleteNetIncome: "DELETE_NET_INCOME",
    updateBudgetCategory: "UPDATE_BUDGET_CATEGORY",
    deleteBudgetCategory: "DELETE_BUDGET_CATEGORY",
    updateCategoryPositions: "UPDATE_CATEGORY_POSITIONS",
    updateConnectedForecasts: "UPDATE_CONNECTED_FORECASTS",
    getBudgetDrivers: "GET_BUDGET_DRIVERS",
    getBudgetSource: "GET_BUDGET_SOURCE",
    getBudgetRefreshPreview: "GET_BUDGET_REFRESH_PREVIEW",
    applyBudgetRefresh: "APPLY_BUDGET_REFRESH",
    getBudgetPublishPreview: "GET_BUDGET_PUBLISH_PREVIEW",
    setBudgetDriver: "SET_BUDGET_DRIVER",
    recalculateBudgetDrivers: "RECALCULATE_BUDGET_DRIVERS",
    setBudgetPeriodStatus: "SET_BUDGET_PERIOD_STATUS",
    createBudget: "CREATE_BUDGET",
    deleteBudget: "DELETE_BUDGET"
}

const initialState = {
    loading: false,
    budgetCards: [],
    budgetSettings: {
        categories: [],
        templates: [],
        netIncomeCalculations: [],
    },
    budgetDrivers: {
        definitions: [],
        drivers: {},
        versions: [],
        periods: [],
        tieOut: null,
    },
    budgetSource: null,
    budgetRefreshPreview: null,
    budgetPublishPreview: null,
    budgetHistory: [],
    budgetHistoryTotal: 0,
    budgetHistoryPage: 1,
    budgetHistoryPageSize: 50,
    selectedBudget: null,
    loadingKeys: [],
    firstLoadComplete: false,
    error: null,
}

export const getBudgetSettings = createAsyncThunk(
    'budgets/getBudgetSettings',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/budget/settings');
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

const budgetError = (error, fallback) => {
    if (typeof error.response?.data === 'string') return error.response.data;
    return error.response?.data?.error || error.response?.data?.errors || fallback;
};

export const getBudgetTemplateSheetLoadTabs = createAsyncThunk(
    'budgets/getBudgetTemplateSheetLoadTabs',
    async ({ url }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/budget/templates/sheet-load/tabs', { params: { url } });
            return data;
        } catch (error) {
            return rejectWithValue(budgetError(error, 'Failed to load spreadsheet tabs'));
        }
    }
);

export const previewBudgetTemplateSheetLoad = createAsyncThunk(
    'budgets/previewBudgetTemplateSheetLoad',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/templates/sheet-load/preview', payload);
            return data;
        } catch (error) {
            return rejectWithValue(budgetError(error, 'Failed to preview sheet template'));
        }
    }
);

export const saveBudgetTemplateSheetLoad = createAsyncThunk(
    'budgets/saveBudgetTemplateSheetLoad',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/templates/sheet-load/save', payload);
            return data;
        } catch (error) {
            return rejectWithValue(budgetError(error, 'Failed to save template draft'));
        }
    }
);

export const publishBudgetTemplateSheetLoad = createAsyncThunk(
    'budgets/publishBudgetTemplateSheetLoad',
    async ({ id, expectedFingerprint }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post(`/api/v1/budget/templates/${id}/publish`, { expectedFingerprint });
            return data;
        } catch (error) {
            return rejectWithValue(budgetError(error, 'Failed to publish template'));
        }
    }
);

export const getBudgetDrivers = createAsyncThunk(
    'budgets/getBudgetDrivers',
    async ({ budgetId }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/budget/drivers', { params: { budgetId } });
            return data;
        } catch (error) {
            return rejectWithValue(budgetError(error, 'Failed to get budget drivers'));
        }
    }
);

export const getBudgetSource = createAsyncThunk(
    'budgets/getBudgetSource',
    async (id, { rejectWithValue }) => {
        try {
            const { data } = await axios.get(`/api/v1/budget/${id}/source`);
            return data;
        } catch (error) {
            return rejectWithValue(budgetError(error, 'Failed to get budget source'));
        }
    }
);

export const getBudgetRefreshPreview = createAsyncThunk(
    'budgets/getBudgetRefreshPreview',
    async (params, { rejectWithValue }) => {
        try {
            const { id, overrideImported = false } = params && typeof params === 'object' ? params : { id: params };
            const { data } = await axios.get(`/api/v1/budget/${id}/refresh/preview`, {
                params: { overrideImported },
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || budgetError(error, 'Failed to preview budget refresh'));
        }
    }
);

export const applyBudgetRefresh = createAsyncThunk(
    'budgets/applyBudgetRefresh',
    async ({ id, expectedPlanUpdatedAt, overrideImported = false }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post(`/api/v1/budget/${id}/refresh`, {
                expected_plan_updated_at: expectedPlanUpdatedAt,
                overrideImported,
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || budgetError(error, 'Failed to refresh budget'));
        }
    }
);

export const getBudgetPublishPreview = createAsyncThunk(
    'budgets/getBudgetPublishPreview',
    async (id, { rejectWithValue }) => {
        try {
            const { data } = await axios.get(`/api/v1/budget/${id}/publish-preview`);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || budgetError(error, 'Failed to preview budget publication'));
        }
    }
);

export const setBudgetDriver = createAsyncThunk(
    'budgets/setBudgetDriver',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/drivers', payload);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || budgetError(error, 'Failed to set budget driver'));
        }
    }
);

export const recalculateBudgetDrivers = createAsyncThunk(
    'budgets/recalculateBudgetDrivers',
    async ({ budgetId }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/drivers/recalculate', { budgetId });
            return data;
        } catch (error) {
            return rejectWithValue(budgetError(error, 'Failed to recalculate budget drivers'));
        }
    }
);

export const setBudgetPeriodStatus = createAsyncThunk(
    'budgets/setBudgetPeriodStatus',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/periods', payload);
            return data;
        } catch (error) {
            if (error.response?.data?.error === 'actuals_not_ready') {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(budgetError(error, 'Failed to update budget period'));
        }
    }
);

export const getBudgetHistory = createAsyncThunk(
    'budgets/getBudgetHistory',
    async ({
        search,
        budgetId,
        userId,
        page,
        pageSize,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/budget-history', { params: {
                search,
                budgetId,
                userId,
                page,
                pageSize,
            }});
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const getBudgetCards = createAsyncThunk(
    'budgets/getBudgetCards',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/budget');
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const getBudgetRequest = createAsyncThunk(
    'budgets/getBudgetRequest',
    async ({ id }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/budget-item', { params: { 
                id,
            }});
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const createBudgetTemplate = createAsyncThunk(
    'budgets/createBudgetTemplate',
    async ({
        name,
        items,
        categories,
    }, { rejectWithValue }) => {
        try {
            const payload = { name };
            if (items && typeof items === 'object') payload.items = items;
            if (Array.isArray(categories)) payload.categories = categories;
            const { data } = await axios.post('/api/v1/budget/create-template', payload);
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const updateBudgetTemplate = createAsyncThunk(
    'budgets/updateBudgetTemplate',
    async ({
        templateId,
        name,
        items,
        categories,
    }, { rejectWithValue }) => {
        try {
            const payload = { templateId, name };
            if (items && typeof items === 'object') payload.items = items;
            if (Array.isArray(categories)) payload.categories = categories;
            const { data } = await axios.post('/api/v1/budget/update-template', payload);
            return data;
        } catch (error) {
            if ('string' === typeof error.response?.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response?.data?.errors || error.response?.data?.error)
            }
        }
    }
)

export const deleteBudgetTemplate = createAsyncThunk(
    'budgets/deleteBudgetTemplate',
    async ({
        templateId,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/delete-template', { templateId });
            return data;
        } catch (error) {
            if ('string' === typeof error.response?.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response?.data?.errors || error.response?.data?.error)
            }
        }
    }
)

export const createBudgetNetIncome = createAsyncThunk(
    'budgets/createBudgetNetIncome',
    async ({
        name,
        items,
    }, { rejectWithValue }) => {
        try {
            const payload = { name, items };
            const { data } = await axios.post('/api/v1/budget/create-net-income', payload);
            return data;
        } catch (error) {
            if ('string' === typeof error.response?.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response?.data?.errors || error.response?.data?.error)
            }
        }
    }
)

export const updateBudgetNetIncome = createAsyncThunk(
    'budgets/updateBudgetNetIncome',
    async ({
        id,
        name,
        items,
    }, { rejectWithValue }) => {
        try {
            const payload = { id, name, items };
            const { data } = await axios.post('/api/v1/budget/update-net-income', payload);
            return data;
        } catch (error) {
            if ('string' === typeof error.response?.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response?.data?.errors || error.response?.data?.error)
            }
        }
    }
)

export const deleteBudgetNetIncome = createAsyncThunk(
    'budgets/deleteBudgetNetIncome',
    async ({
        id,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/delete-net-income', { id });
            return data;
        } catch (error) {
            if (error.response?.status === 409) {
                return rejectWithValue({
                    error: error.response.data.error || 'Net income calculation cannot be deleted',
                    budgets: error.response.data.budgets || []
                });
            }
            if ('string' === typeof error.response?.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response?.data?.errors || error.response?.data?.error || 'Failed to delete net income calculation')
            }
        }
    }
)

export const createBudgetCategory = createAsyncThunk(
    'budgets/createBudgetCategory',
    async ({
        name,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/create-category', { name });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const updateBudgetCategory = createAsyncThunk(
    'budgets/updateBudgetCategory',
    async ({
        categoryId,
        name,
        colors,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/update-category', { categoryId, name, colors });
            return data;
        } catch (error) {
            if ('string' === typeof error.response?.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response?.data?.errors || error.response?.data?.error)
            }
        }
    }
)

export const deleteBudgetCategory = createAsyncThunk(
    'budgets/deleteBudgetCategory',
    async ({
        categoryId,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/delete-category', { categoryId });
            return data;
        } catch (error) {
            if (error.response?.status === 409) {
                // Conflict - category is used in budgets
                return rejectWithValue({
                    error: error.response.data.error || 'Category cannot be deleted',
                    budgets: error.response.data.budgets || []
                });
            }
            if ('string' === typeof error.response?.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response?.data?.errors || error.response?.data?.error || 'Failed to delete category')
            }
        }
    }
)

export const updateCategoryPositions = createAsyncThunk(
    'budgets/updateCategoryPositions',
    async ({
        positionUpdates,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/update-category-positions', { positionUpdates });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const updateConnectedForecasts = createAsyncThunk(
    'budgets/updateConnectedForecasts',
    async ({
        connectedForecasts
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/update-forecasts', { connectedForecasts });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const duplicateBudgetRequest = createAsyncThunk(
    'budgets/duplicateBudgetRequest',
    async ({
        budgetId,
        name,
        copyAll,
        copyValues,
        copyRollups,
        copyFormulas
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/duplicate-budget', { 
                budgetId, 
                name,
                copyAll,
                copyValues,
                copyRollups,
                copyFormulas
            });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    } 
)

export const createBudgetRequest = createAsyncThunk(
    'budgets/createBudgetRequest',
    async ({
        id,
        name,
        period,
        selectedForecast,
        activeCategories,
        selectedTemplate,
        selectedNetIncome
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/create', { id, name, period, selectedForecast, activeCategories, selectedTemplate, selectedNetIncome });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const updateBudgetPublishStatus = createAsyncThunk(
    'budgets/updateBudgetPublishStatus',
    async ({ id, publishStatus }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/budget/update-publish-status', { id, publishStatus });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const deleteBudgetRequest = createAsyncThunk(
    'budgets/deleteBudgetRequest',
    async ({
        id
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.delete(`/api/v1/budget/delete/${id}`);
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const budgetSlice = createSlice({
    name: 'budgets',
    initialState,
    reducers: {
        clearSelectedBudget: (state) => {
            state.selectedBudget = null;
        },
        clearBudgetHistory: (state) => {
            state.budgetHistory = [];
            state.budgetHistoryTotal = 0;
            state.budgetHistoryPage = 1;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getBudgetSettings.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.getBudgetSettings];
            })
            .addCase(getBudgetSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.budgetSettings = action.payload || { categories: [], templates: [], netIncomeCalculations: [] };
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetSettings, state.loadingKeys);
            })
            .addCase(getBudgetSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.budgetSettings = { categories: [], templates: [], netIncomeCalculations: [] };
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetSettings, state.loadingKeys);
            })
            .addCase(getBudgetHistory.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.getBudgetHistory];
            })
            .addCase(getBudgetHistory.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                const requestedPage = Number(action.meta?.arg?.page || 1);
                const items = action.payload.items || [];
                const shouldAppend = requestedPage > 1 && !action.meta?.arg?.replace;
                state.budgetHistory = shouldAppend ? [...state.budgetHistory, ...items] : items;
                state.budgetHistoryTotal = Number(action.payload.total || 0);
                state.budgetHistoryPage = Number(action.payload.page || requestedPage);
                state.budgetHistoryPageSize = Number(action.payload.pageSize || action.meta?.arg?.pageSize || 50);
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetHistory, state.loadingKeys);
            })
            .addCase(getBudgetHistory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetHistory, state.loadingKeys);
            })
            .addCase(getBudgetRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.getBudgetCard];
            })
            .addCase(getBudgetRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.selectedBudget = action.payload.item;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetCard, state.loadingKeys);
            })
            .addCase(getBudgetRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.selectedBudget = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetCard, state.loadingKeys);
            })
            .addCase(getBudgetCards.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.getBudgetCards];
            })
            .addCase(getBudgetCards.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.budgetCards = action.payload.items;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetCards, state.loadingKeys);
            })
            .addCase(getBudgetCards.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.budgetCards = [];
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetCards, state.loadingKeys);
            })
            .addCase(createBudgetTemplate.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.createTemplate];
            })
            .addCase(createBudgetTemplate.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.createTemplate, state.loadingKeys);
                toast.success(`Template created successfully.`, { position: "bottom-right" })
            })
            .addCase(createBudgetTemplate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.createTemplate, state.loadingKeys);
                toast.error(
                    `Failed to create template. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(updateBudgetTemplate.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.updateTemplate];
            })
            .addCase(updateBudgetTemplate.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.updateTemplate, state.loadingKeys);
                toast.success(`Template updated successfully.`, { position: "bottom-right" })
            })
            .addCase(updateBudgetTemplate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.updateTemplate, state.loadingKeys);
                toast.error(
                    `Failed to update template. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(deleteBudgetTemplate.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.deleteTemplate];
            })
            .addCase(deleteBudgetTemplate.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.deleteTemplate, state.loadingKeys);
                toast.success(`Template deleted successfully.`, { position: "bottom-right" })
            })
            .addCase(deleteBudgetTemplate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.deleteTemplate, state.loadingKeys);
                toast.error(
                    `Failed to delete template. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(createBudgetNetIncome.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.createNetIncome];
            })
            .addCase(createBudgetNetIncome.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.createNetIncome, state.loadingKeys);
                toast.success(`Net income calculation created successfully.`, { position: "bottom-right" })
            })
            .addCase(createBudgetNetIncome.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.createNetIncome, state.loadingKeys);
                toast.error(
                    `Failed to create net income calculation. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(updateBudgetNetIncome.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.updateNetIncome];
            })
            .addCase(updateBudgetNetIncome.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.updateNetIncome, state.loadingKeys);
                toast.success(`Net income calculation updated successfully.`, { position: "bottom-right" })
            })
            .addCase(updateBudgetNetIncome.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.updateNetIncome, state.loadingKeys);
                toast.error(
                    `Failed to update net income calculation. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(deleteBudgetNetIncome.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.deleteNetIncome];
            })
            .addCase(deleteBudgetNetIncome.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.deleteNetIncome, state.loadingKeys);
                toast.success(`Net income calculation deleted successfully.`, { position: "bottom-right" })
            })
            .addCase(deleteBudgetNetIncome.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.deleteNetIncome, state.loadingKeys);
                toast.error(
                    `Failed to delete net income calculation. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(createBudgetCategory.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.createBudgetCategory];
            })
            .addCase(createBudgetCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.createBudgetCategory, state.loadingKeys);
                toast.success(`Category created successfully.`, { position: "bottom-right" })
            })
            .addCase(createBudgetCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.createBudgetCategory, state.loadingKeys);
                toast.error(
                    `Failed to create category. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(updateBudgetCategory.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.updateBudgetCategory];
            })
            .addCase(updateBudgetCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.updateBudgetCategory, state.loadingKeys);
                
                // Update the category in the local state
                if (action.payload?.category && state.budgetSettings?.categories) {
                    const categoryIndex = state.budgetSettings.categories.findIndex(
                        cat => cat.id === action.payload.category.id
                    );
                    if (categoryIndex !== -1) {
                        state.budgetSettings.categories[categoryIndex] = {
                            ...state.budgetSettings.categories[categoryIndex],
                            ...action.payload.category
                        };
                    }
                }
                
                toast.success(`Budget category updated successfully.`, { position: "bottom-right" })
            })
            .addCase(updateBudgetCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.updateBudgetCategory, state.loadingKeys);
                toast.error(
                    `Failed to update budget category. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(updateCategoryPositions.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.updateCategoryPositions];
            })
            .addCase(updateCategoryPositions.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.updateCategoryPositions, state.loadingKeys);
                toast.success(`Category positions updated successfully.`, { position: "bottom-right" })
            })
            .addCase(updateCategoryPositions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.updateCategoryPositions, state.loadingKeys);
                toast.error(
                    `Failed to update category positions. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(deleteBudgetCategory.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.deleteBudgetCategory];
            })
            .addCase(deleteBudgetCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.deleteBudgetCategory, state.loadingKeys);
                toast.success(`Category deleted successfully.`, { position: "bottom-right" })
            })
            .addCase(deleteBudgetCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.deleteBudgetCategory, state.loadingKeys);
                // Don't show toast here - we'll handle it in the component to show the info modal
            })
            .addCase(getBudgetDrivers.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.getBudgetDrivers];
            })
            .addCase(getBudgetDrivers.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetDrivers, state.loadingKeys);
                state.budgetDrivers = {
                    ...state.budgetDrivers,
                    ...action.payload,
                };
            })
            .addCase(getBudgetDrivers.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetDrivers, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(getBudgetSource.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.getBudgetSource];
            })
            .addCase(getBudgetSource.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetSource, state.loadingKeys);
                state.budgetSource = action.payload;
            })
            .addCase(getBudgetSource.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetSource, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(getBudgetRefreshPreview.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.getBudgetRefreshPreview];
            })
            .addCase(getBudgetRefreshPreview.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetRefreshPreview, state.loadingKeys);
                state.budgetRefreshPreview = action.payload;
            })
            .addCase(getBudgetRefreshPreview.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetRefreshPreview, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(applyBudgetRefresh.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.applyBudgetRefresh];
            })
            .addCase(applyBudgetRefresh.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.applyBudgetRefresh, state.loadingKeys);
                state.budgetRefreshPreview = action.payload?.preview || null;
                toast.success('Budget refreshed from Demand Plan.', { position: 'bottom-right' });
            })
            .addCase(applyBudgetRefresh.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.applyBudgetRefresh, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(getBudgetPublishPreview.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.getBudgetPublishPreview];
            })
            .addCase(getBudgetPublishPreview.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetPublishPreview, state.loadingKeys);
                state.budgetPublishPreview = action.payload;
            })
            .addCase(getBudgetPublishPreview.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.getBudgetPublishPreview, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(setBudgetDriver.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.setBudgetDriver];
            })
            .addCase(setBudgetDriver.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.setBudgetDriver, state.loadingKeys);
                state.budgetDrivers = {
                    ...state.budgetDrivers,
                    tieOut: action.payload?.tieOut || null,
                };
            })
            .addCase(setBudgetDriver.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.setBudgetDriver, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(recalculateBudgetDrivers.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.recalculateBudgetDrivers];
            })
            .addCase(recalculateBudgetDrivers.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.recalculateBudgetDrivers, state.loadingKeys);
                state.budgetDrivers = { ...state.budgetDrivers, tieOut: action.payload?.tieOut || null };
            })
            .addCase(recalculateBudgetDrivers.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.recalculateBudgetDrivers, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(setBudgetPeriodStatus.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.setBudgetPeriodStatus];
            })
            .addCase(setBudgetPeriodStatus.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.setBudgetPeriodStatus, state.loadingKeys);
                const month = action.payload?.month;
                const periods = state.budgetDrivers.periods || [];
                const existing = periods.find((period) => String(period.month).startsWith(month));
                if (existing) existing.status = action.payload.status;
                else state.budgetDrivers.periods = [...periods, { month: `${month}-01`, status: action.payload.status }];
            })
            .addCase(setBudgetPeriodStatus.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.setBudgetPeriodStatus, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(updateConnectedForecasts.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.updateConnectedForecasts];
            })
            .addCase(updateConnectedForecasts.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.updateConnectedForecasts, state.loadingKeys);
                toast.success(`Connected forecasts updated successfully.`, { position: "bottom-right" })
            })
            .addCase(updateConnectedForecasts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.updateConnectedForecasts, state.loadingKeys);
                toast.error(
                    `Failed to update connected forecasts. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(createBudgetRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.createBudget];
            })
            .addCase(createBudgetRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.createBudget, state.loadingKeys);
                toast.success(`Budget created successfully.`, { position: "bottom-right" })
            })
            .addCase(createBudgetRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.createBudget, state.loadingKeys);
                toast.error(
                    `Failed to create budget. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(deleteBudgetRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.deleteBudget];
            })
            .addCase(deleteBudgetRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.deleteBudget, state.loadingKeys);
                toast.success(`Budget deleted successfully.`, { position: "bottom-right" })
            })
            .addCase(deleteBudgetRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.deleteBudget, state.loadingKeys);
                toast.error(
                    `Budget can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(duplicateBudgetRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, BudgetLoadingKeys.duplicateBudget];
            })
            .addCase(duplicateBudgetRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.duplicateBudget, state.loadingKeys);
                toast.success(`Budget duplicated successfully.`, { position: "bottom-right" })
            })
            .addCase(duplicateBudgetRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(BudgetLoadingKeys.duplicateBudget, state.loadingKeys);
                toast.error(
                    `Failed to duplicate budget. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
    }
})
export const { clearSelectedBudget, clearBudgetHistory } = budgetSlice.actions;
export default budgetSlice.reducer 